import React, { lazy, Suspense, useMemo, useState } from 'react';
import { Card, Input, Skeleton, Tabs } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { nonNull } from '@helpers/non-null';
import { useOrganization } from '@components/OrganizationBoundary';
import { apiClient } from '@api/client';
import { IntegrationItemDto, OrganizationItemDto } from '@api/Api';

const AvailableIntegrations = lazy(() => import('./AvailableIntegrations'));
const InstalledIntegrations = lazy(() => import('./InstalledIntegrations'));

export default function IntegrationTab() {
  const params = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization(true);
  const org = nonNull(currentOrganization.entity);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    data: installedIntegrations,
    isLoading,
    refetch: refetchInstalledIntegrations
  } = useQuery({
    queryKey: ['integrations', 'installed'],
    queryFn: async () => {
      const { data } =
        await apiClient.integration.integrationControllerGetInsalledRegistrations(
          { organizationId: org.id }
        );
      return data.integrations;
    }
  });

  const tabs = useMemo(() => {
    const skeletons = [1, 2, 3, 4, 5, 6].map((x) => (
      <Card key={x}>
        <Card.Meta
          title={<Skeleton.Input size="small" />}
          description={<Skeleton />}
          avatar={<Skeleton.Image />}
        />
      </Card>
    ));
    const tabShell = (
      render: (
        searchQuery: string,
        integrations: IntegrationItemDto[],
        organizationFeatures: OrganizationItemDto['features']
      ) => React.ReactElement
    ) => (
      <div>
        <Input.Search
          placeholder="Search for integrations"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
            alignItems: 'stretch',
            gap: 16,
            marginTop: 16
          }}
        >
          {isLoading ? (
            skeletons
          ) : (
            <Suspense fallback={skeletons}>
              {render(
                searchQuery,
                nonNull(installedIntegrations),
                org.features
              )}
            </Suspense>
          )}
        </div>
      </div>
    );
    return [
      {
        label: 'Available',
        key: 'available',
        children: tabShell(
          (searchQuery, installedIntegrations, organizationFeatures) => (
            <AvailableIntegrations
              searchQuery={searchQuery}
              organizationId={org.id}
              organizationFeatures={organizationFeatures}
              installedIntegrations={installedIntegrations}
              refetchInstalledIntegrations={refetchInstalledIntegrations}
            />
          )
        )
      },
      {
        label: 'Installed',
        key: 'installed',
        children: tabShell((searchQuery, installedIntegrations) => (
          <InstalledIntegrations
            searchQuery={searchQuery}
            list={nonNull(installedIntegrations)}
            refetchInstalledIntegrations={refetchInstalledIntegrations}
          />
        ))
      }
    ];
  }, [
    org.id,
    org.features,
    searchQuery,
    isLoading,
    installedIntegrations,
    refetchInstalledIntegrations
  ]);
  const tabParam = params.integrationTab;
  const tab = useMemo(() => {
    return tabs.find((x) => x.key === tabParam)?.key ?? tabs[0].key;
  }, [tabs, tabParam]);
  return (
    <Tabs
      items={tabs}
      activeKey={tab}
      onChange={(v) => navigate(`integrations/${v}`)}
      tabPosition="left"
    />
  );
}
